import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/config';
import { supabase } from '@/lib/supabase';

export type TitleMetaSuggestion = {
  title: string;
  titleLength: number;
  meta: string;
  metaLength: number;
  reasoning: string;
};

export type TitleMetaRewriteResult = {
  suggestions: TitleMetaSuggestion[];
};

export type ContentBriefResult = {
  targetWordCount: number;
  suggestedTitle: string;
  outline: string[];
  keyPoints: string[];
  relatedKeywords: string[];
  contentAngle: string;
  searchIntent: string;
};

export type SerpCompetitor = {
  url: string;
  title: string;
  meta: string;
  wordCount: number;
  headings: string[];
  topics: string[];
  serpPosition?: number;
};

export type GoogleSerpResult = {
  url: string;
  title: string;
  snippet: string;
};

export type SerpSummary = {
  avgWordCount: number;
  commonTopics: string[];
  contentGaps: string[];
  dominantSearchIntent: string;
  commonHeadingPatterns: string[];
};

export type SerpFeatures = {
  peopleAlsoAsk: string[];
  relatedSearches: string[];
  featuredSnippet: { title: string; url: string; snippet: string } | null;
  totalResults: number;
};

export type SerpAnalysisResult = {
  competitors: SerpCompetitor[];
  summary: SerpSummary;
  _serpResults?: GoogleSerpResult[];
  _serpFeatures?: SerpFeatures;
  _extractions?: Array<{
    url: string;
    title: string;
    meta: string;
    wordCount: number;
    headings: string[];
    serpPosition?: number;
  }>;
};

export type ContentScoreDetails = {
  score: number;
  details: string;
};

export type ContentOptimizationResult = {
  overallScore: number;
  scores: {
    contentDepth: ContentScoreDetails;
    keywordUsage: ContentScoreDetails;
    headingStructure: ContentScoreDetails;
    topicCoverage: ContentScoreDetails;
  };
  recommendations: Array<{
    priority: 'high' | 'medium' | 'low';
    action: string;
    impact: string;
  }>;
};

export type OutlineItem = {
  level: string;
  text: string;
  notes: string;
  estimatedWords: number;
};

export type EnhancedContentBriefResult = {
  targetWordCount: number;
  suggestedTitle: string;
  suggestedMeta: string;
  suggestedSlug: string;
  outline: OutlineItem[];
  topicsToCover: string[];
  questionsToAnswer: string[];
  internalLinkSuggestions: string[];
  searchIntent: string;
  contentAngle: string;
  relatedKeywords: string[];
  competitorInsights: string;
};

export type InternalLinkSuggestion = {
  fromPage: string;
  toPage: string;
  anchorText: string;
  context: string;
  relevanceScore: number;
};

export type InternalLinkFinderResult = {
  suggestions: InternalLinkSuggestion[];
  summary: {
    totalOpportunities: number;
    highPriority: number;
  };
};

export type AnalyzedOpportunity = {
  opportunityType: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  metric: string;
  impactScore: number;
  fixGuidance: {
    steps: string[];
    estimatedImpact: string;
    difficulty: 'easy' | 'medium' | 'hard';
    timeEstimate: string;
  };
};

export type AnalyzeOpportunitiesResult = {
  opportunities: AnalyzedOpportunity[];
};

export type LivePositionResult = {
  livePosition: number | null;
  liveUrl: string | null;
  totalResults: number;
  topResults: Array<{ position: number; url: string; title: string; domain: string }>;
  checkedAt: string;
};

export type FixGuidanceResult = {
  steps: string[];
  estimatedImpact: string;
  difficulty: 'easy' | 'medium' | 'hard';
  timeEstimate: string;
};

export type AiSuggestionItem = {
  type: string;
  label: string;
  value: string;
  reasoning: string;
};

export type OpportunitySuggestionsResult = {
  suggestions: AiSuggestionItem[];
};

async function invokeAiFunction(endpoint: string, body: Record<string, unknown>): Promise<any> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;

  const res = await fetch(`${SUPABASE_URL}/functions/v1/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token ?? ''}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ ...body, userId }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `AI function error ${res.status}`);
  }

  return res.json();
}

const seoAiService = {
  async rewriteTitleMeta(params: {
    keyword: string;
    currentTitle: string;
    currentMeta: string;
    pageUrl?: string;
    position?: number;
    ctr?: number;
    impressions?: number;
  }): Promise<TitleMetaRewriteResult> {
    return invokeAiFunction('seo-content', {
      action: 'rewriteTitleMeta',
      ...params,
    });
  },

  async generateContentBrief(params: {
    keyword: string;
    currentPosition?: number;
    topCompetitors?: string[];
  }): Promise<ContentBriefResult> {
    return invokeAiFunction('seo-content', {
      action: 'contentBrief',
      ...params,
    });
  },

  async analyzeSerpCompetitors(params: {
    keyword: string;
    competitorUrls?: string[];
    userSiteUrl?: string;
  }): Promise<SerpAnalysisResult> {
    return invokeAiFunction('seo-serp', {
      action: 'serpAnalysis',
      ...params,
    });
  },

  async optimizeContent(params: {
    keyword: string;
    pageUrl: string;
    serpSummary: SerpSummary;
  }): Promise<ContentOptimizationResult> {
    return invokeAiFunction('seo-content', {
      action: 'contentOptimizer',
      ...params,
    });
  },

  async generateEnhancedContentBrief(params: {
    keyword: string;
    serpSummary: SerpSummary;
    currentPosition?: number;
    gscMetrics?: {
      clicks: number;
      impressions: number;
      ctr: number;
    };
  }): Promise<EnhancedContentBriefResult> {
    return invokeAiFunction('seo-content', {
      action: 'contentBriefEnhanced',
      ...params,
    });
  },

  async findInternalLinks(params: {
    keyword: string;
    targetPage: string;
    sitePages: Array<{ page: string; keyword?: string }>;
  }): Promise<InternalLinkFinderResult> {
    return invokeAiFunction('seo-links', {
      action: 'internalLinkFinder',
      ...params,
    });
  },

  async checkLivePosition(params: {
    keyword: string;
    siteUrl: string;
  }): Promise<LivePositionResult> {
    return invokeAiFunction('seo-serp', {
      action: 'livePosition',
      ...params,
    });
  },

  async analyzeOpportunities(params: {
    keyword: string;
    position: number | null;
    ctr: number | null;
    clicks: number | null;
    impressions: number | null;
    days: number;
    rankingPages: Array<{
      page: string;
      position: number;
      clicks: number;
      impressions: number;
      ctr: number;
    }>;
    serpSummary: SerpSummary | null;
    positionHistory: Array<{ date: string; position: number }>;
    rankingPageUrl?: string | null;
    sitePages?: Array<{ page: string; keyword?: string }>;
    livePosition?: LivePositionResult;
  }): Promise<AnalyzeOpportunitiesResult> {
    return invokeAiFunction('seo-analysis', {
      action: 'analyzeOpportunities',
      ...params,
    });
  },

  async getOpportunitySuggestions(params: {
    keyword: string;
    opportunityType: string;
    opportunityTitle: string;
    opportunityDescription: string;
    position: number | null;
    ctr: number | null;
    clicks: number | null;
    impressions: number | null;
    serpSummary: SerpSummary | null;
    pageUrl?: string;
    evidence?: Record<string, any>;
    competitors?: SerpCompetitor[];
    peopleAlsoAsk?: string[];
    relatedSearches?: string[];
  }): Promise<OpportunitySuggestionsResult> {
    return invokeAiFunction('seo-analysis', {
      action: 'getOpportunitySuggestions',
      ...params,
    });
  },

  async getFixGuidance(params: {
    keyword: string;
    opportunityType: string;
    opportunityTitle: string;
    position: number | null;
    ctr: number | null;
    clicks: number | null;
    impressions: number | null;
    serpSummary: SerpSummary | null;
  }): Promise<FixGuidanceResult> {
    return invokeAiFunction('seo-analysis', {
      action: 'getFixGuidance',
      ...params,
    });
  },

  async generateSchema(params: { pageUrl: string; keyword?: string; pageType?: string }): Promise<{
    schemaTypes: string[];
    reasoning: string;
    jsonLd: Record<string, any>;
    jsonLdString: string;
    estimatedRichResults: string[];
  }> {
    return invokeAiFunction('seo-content', {
      action: 'generateSchema',
      ...params,
    });
  },
  async analyzeServicePageHealth(params: {
    pageUrl: string;
    sitePages?: Array<{ page: string; keyword?: string }>;
  }): Promise<ServicePageHealthResult> {
    return invokeAiFunction('seo-analysis', {
      action: 'analyzeServicePageHealth',
      ...params,
    });
  },
};

export type ServicePageHealthCheck = {
  pass: boolean | null;
  detail: string;
  wordCount?: number;
  siblingCount?: number;
};

export type ServicePageHealthResult = {
  isServicePage: boolean;
  checks: {
    faqSchema: ServicePageHealthCheck;
    serviceSchema: ServicePageHealthCheck;
    pricing: ServicePageHealthCheck;
    titleFreshness: ServicePageHealthCheck;
    contentDepth: ServicePageHealthCheck;
    uniqueness: ServicePageHealthCheck;
  };
  wordCount: number;
  title: string;
  meta: string;
  hasAccordion: boolean;
  faqCount: number;
  existingFaqs: Array<{ question: string; answer: string }>;
  existingProductSchema: any | null;
};

export default seoAiService;
